// Wiki documentation content
// This file contains all the documentation for the in-app wiki viewer

export const wikiContent: Record<string, string> = {
  home: `# PTC Tax Lifecycle Wiki

Welcome to the PTC Tax Lifecycle application documentation. This wiki provides comprehensive guidance for staff, preparers, and reviewers who use the system to manage tax returns from initial client contact through final filing.

---

## Quick Start

1. **Log in** using your Microsoft account at the application URL
2. **View your dashboard** to see pending tasks and return statistics
3. **Access tax returns** to manage client returns through the lifecycle
4. **Send notifications** to keep clients informed of their return status

---

## Application Overview

PTC Tax Lifecycle is a comprehensive tax return management system that tracks returns from client outreach through completion and filing. The application provides:

- **Return Tracking** - Monitor every return through 8 lifecycle stages
- **Automated Notifications** - Keep clients and staff informed automatically
- **Document Management** - Collect and organize client documents
- **E-Signature Integration** - Collect signatures electronically via Zoho Sign
- **Client Portal** - Allow clients to upload documents and track status
- **Outreach Campaigns** - Send personalized tax season communications
- **Inquiry Management** - Track and respond to client questions

---

## Tax Return Lifecycle

Returns progress through these stages:

\`\`\`
Scheduled
    │
    ├──► Pending Resolution (if multi-attendee needs clarification)
    │
    ▼
Documents Pending
    │
    ▼
In Preparation
    │
    ▼
In Review
    │
    ▼
Ready for Signature
    │
    ▼
E-Filed
    │
    ▼
Completed
\`\`\`

Use the **Tax Returns** tab above for detailed information about each stage.

---

## System Requirements

- **Browser**: Microsoft Edge, Google Chrome, or Firefox (latest versions)
- **Account**: Microsoft (Azure AD) account with appropriate permissions
- **Internet**: Stable internet connection required

---

## User Roles

| Role | Capabilities |
|------|-------------|
| **Preparer** | Create and prepare tax returns, upload documents, send notifications |
| **Reviewer** | Review prepared returns, approve or request changes |
| **Admin** | Full access including configuration and user management |

---

## Support

For technical support or questions about the application:

- **Email**: support@ptcfinancial.com
- **Phone**: Contact your administrator for the support line
`,

  'getting-started': `# Getting Started

This guide covers logging into the PTC Tax Lifecycle application, navigating the dashboard, and understanding your user role.

---

## Logging In

The application uses Microsoft Azure AD for authentication. To log in:

1. Navigate to the application URL
2. Click **Sign in with Microsoft**
3. Enter your Microsoft work account credentials
4. Complete any multi-factor authentication prompts
5. You'll be redirected to your dashboard

> **Note**: You must have a Microsoft account that has been granted access to the application. Contact your administrator if you cannot log in.

---

## Dashboard Overview

After logging in, you'll see your dashboard with key statistics and quick actions.

### Dashboard Statistics

| Stat | Description |
|------|-------------|
| **Total Returns** | All tax returns in the system for the current tax year |
| **Documents Pending** | Returns waiting for client documents |
| **In Preparation** | Returns being actively prepared |
| **In Review** | Returns submitted for quality review |
| **Ready for Signature** | Returns awaiting client e-signature |
| **E-File Rejections** | Returns rejected by IRS that need attention |
| **Completed This Week** | Returns finished in the past 7 days |

### Quick Actions

From the dashboard, you can:

- View returns requiring immediate attention
- Access recently updated returns
- See your assigned returns (preparers) or returns awaiting review (reviewers)

---

## Main Navigation

The main menu provides access to all application features:

### Dashboard
Your home screen with statistics and quick access to important items.

### Tax Returns
The complete list of all tax returns. Use filters and sorting to find specific returns.

### Outreach
Send email and SMS campaigns to clients for tax season kickoff, document reminders, and other communications.

### Inquiries
View and respond to client questions received via email. Track inquiry status and manage responses.

### Drake Upload
Upload Drake software PDF outputs to extract document checklists and client information.

### Calendar
View scheduled appointments synced from Dynamics 365. See upcoming client meetings.

### Workload
Review preparer assignments and workload distribution across the team.

---

## User Roles

Your role determines what you can do in the application.

### Preparer

Preparers handle the day-to-day work of creating and completing tax returns.

**Capabilities:**
- Create new tax returns
- Upload and manage documents
- Update return status through preparation stages
- Send notifications to clients
- Add notes to returns
- Submit returns for review

**Typical Workflow:**
1. Review assigned returns on dashboard
2. Collect and organize client documents
3. Prepare the tax return
4. Submit for review when complete

### Reviewer

Reviewers ensure quality and accuracy before returns are sent for signature.

**Capabilities:**
- All preparer capabilities
- Review submitted returns
- Approve returns or request changes
- Move returns to "Ready for Signature"
- Add review notes

**Typical Workflow:**
1. Check returns awaiting review
2. Examine return details and documents
3. Approve or request changes with notes
4. Track approved returns through signature and filing

### Admin

Administrators have full system access and can manage configuration.

**Capabilities:**
- All reviewer capabilities
- Access system configuration
- Manage user permissions
- View all returns regardless of assignment
- Access reporting and analytics

---

## Understanding Return Status

Returns move through these stages:

| Status | What It Means |
|--------|---------------|
| **Scheduled** | Appointment created, return initialized |
| **Pending Resolution** | Multi-attendee appointment needs clarification |
| **Documents Pending** | Waiting for client to submit documents |
| **In Preparation** | Preparer actively working on return |
| **In Review** | Submitted for reviewer approval |
| **Ready for Signature** | Approved, awaiting client e-signature |
| **E-Filed** | Submitted to IRS, awaiting acceptance |
| **Completed** | IRS accepted, return finished |

---

## Tips for New Users

1. **Check your dashboard daily** - It shows items requiring your attention
2. **Use filters** - The Tax Returns page has powerful filtering to find specific returns
3. **Add notes** - Document important information on returns for future reference
4. **Send notifications promptly** - Keep clients informed when their status changes
5. **Review the status history** - See what actions have been taken on a return
`,

  'tax-returns': `# Tax Returns

This guide explains how tax returns flow through the system, from initial scheduling to final completion.

---

## Tax Return Lifecycle

Every return follows a defined path through the system. Understanding this lifecycle helps you manage returns efficiently.

### Lifecycle Flow

\`\`\`
                    ┌─────────────────────┐
                    │      Scheduled      │
                    └──────────┬──────────┘
                               │
              ┌────────────────┴────────────────┐
              │                                 │
              ▼                                 ▼
┌─────────────────────────┐       ┌─────────────────────────┐
│   Pending Resolution    │       │   Documents Pending     │
│ (multi-attendee only)   │       └────────────┬────────────┘
└─────────────┬───────────┘                    │
              │                                │
              └────────────────┬───────────────┘
                               │
                               ▼
                    ┌─────────────────────┐
                    │   In Preparation    │
                    └──────────┬──────────┘
                               │
                               ▼
                    ┌─────────────────────┐
                    │      In Review      │◄────┐
                    └──────────┬──────────┘     │
                               │                │
                   ┌───────────┴───────────┐    │
                   │                       │    │
            (approved)              (changes needed)
                   │                       │    │
                   ▼                       └────┘
         ┌─────────────────────┐
         │ Ready for Signature │
         └──────────┬──────────┘
                    │
                    ▼
         ┌─────────────────────┐
         │       E-Filed       │
         └──────────┬──────────┘
                    │
                    ▼
         ┌─────────────────────┐
         │      Completed      │
         └─────────────────────┘
\`\`\`

---

## Lifecycle Stages

### Scheduled

**What happens:** An appointment is created in Dynamics 365, and the system creates a corresponding tax return record.

**Actions available:**
- View appointment details
- Assign a preparer
- Add initial notes

**Next step:** Move to "Documents Pending" when ready to collect documents, or "Pending Resolution" if the appointment has multiple attendees requiring clarification.

---

### Pending Resolution

**What happens:** The appointment has multiple attendees (such as a married couple), and staff must determine whether to create a joint return or separate returns.

**Actions available:**
- Review attendee information
- Decide joint vs. separate filing
- Create additional returns if needed
- Resolve to proceed

**Next step:** Once resolved, the return moves to "Documents Pending."

> **Example:** An appointment shows John Smith and Jane Smith. Staff confirms they're married filing jointly, so one joint return is created.

---

### Documents Pending

**What happens:** The return is waiting for the client to submit their tax documents.

**Actions available:**
- Send document request notifications
- View uploaded documents
- Check document checklist (if Drake extraction available)
- Mark documents as received
- Upload documents on behalf of client

**Notifications triggered:**
- "Documents Pending" notification sent to client (email + SMS)

**Next step:** When all required documents are received, move to "In Preparation."

---

### In Preparation

**What happens:** The preparer is actively working on the tax return.

**Actions available:**
- Review all documents
- Prepare the return in tax software
- Add notes about preparation
- Upload prepared return documents
- Send for review when complete

**Notifications triggered:**
- "Documents Received" notification sent to preparer when all documents arrive

**Next step:** When preparation is complete, submit for review to move to "In Review."

---

### In Review

**What happens:** A reviewer examines the prepared return for accuracy and completeness.

**Actions available:**
- Review return details and documents
- Approve the return
- Request changes (sends back to preparer)
- Add review notes

**Notifications triggered:**
- "Ready for Review" notification sent to assigned reviewer

**Outcomes:**
- **Approved:** Return moves to "Ready for Signature"
- **Changes Needed:** Return moves back to "In Preparation" with reviewer notes

---

### Ready for Signature

**What happens:** The return is approved and awaiting the client's electronic signature on Form 8879.

**Actions available:**
- Send e-signature request via Zoho Sign
- Resend signature request if needed
- Track signature status
- Upload signed documents manually if needed

**Notifications triggered:**
- "Ready for Signature" notification sent to client (email + SMS)
- E-signature request email sent via Zoho Sign

**Next step:** Once the client signs, the return moves to "E-Filed."

---

### E-Filed

**What happens:** The return has been submitted to the IRS and is awaiting acceptance.

**Actions available:**
- Track IRS acknowledgment status
- View rejection details if rejected
- Correct and resubmit rejected returns

**E-file statuses:**
- **Pending** - Submitted, awaiting IRS response
- **Accepted** - IRS accepted the return
- **Rejected** - IRS rejected; review rejection reason

**Notifications triggered:**
- "E-File Accepted" notification sent to client when IRS accepts (email + SMS)

**Next step:** When IRS accepts the return, it moves to "Completed."

---

### Completed

**What happens:** The return is fully complete. The IRS has accepted the return and all work is finished.

**Actions available:**
- View final return summary
- Access refund/payment information
- Download all documents
- Add final notes if needed

**Notifications triggered:**
- "Results Summary" notification sent to client with refund/payment details

---

## Managing Tax Returns

### Viewing the Tax Returns List

Navigate to **Tax Returns** to see all returns. The list shows:

- Client name
- Tax year
- Current status
- Assigned preparer
- Assigned reviewer
- Last updated date

### Filtering Returns

Use filters to find specific returns:

| Filter | Options |
|--------|---------|
| **Status** | Any lifecycle stage |
| **Tax Year** | 2024, 2025, etc. |
| **Preparer** | Staff member assigned |
| **Reviewer** | Reviewer assigned |
| **Search** | Client name or ID |

### Sorting Presets

Quick sorting options:

- **Recently Updated** - Most recent activity first
- **Oldest First** - Longest waiting returns
- **Docs Pending** - Returns waiting for documents
- **Ready for Review** - Returns awaiting review
- **E-File Pending** - Returns awaiting IRS acceptance

---

## Tax Return Details

Click any return to view its complete information.

### Overview Tab

- **Client Information:** Name, contact details, filing type
- **Status:** Current stage with status history
- **Appointment:** Scheduled date and appointment type
- **Assignment:** Preparer and reviewer
- **Results:** Refund or payment amount (when available)
- **E-file Status:** IRS acceptance status
- **Signature Status:** E-signature completion

### Documents Tab

- All uploaded documents
- Document classification
- Upload source (client portal, staff upload, email)
- Download and preview options

### Workflow Tab

- Visual status progression
- Complete status change history
- Each change shows: date, user, previous status, new status, notes

### Billing Tab

- Invoice status and amount
- Payment URL for client
- Preparer bill tracking

### Activity Tab

- Internal notes and comments
- Timestamp and author for each note
- Mark important notes for visibility

---

## Common Actions

### Updating Status

1. Open the tax return
2. Click the **Status** dropdown
3. Select the new status
4. Add notes explaining the change (recommended)
5. Click **Update**

Certain status changes trigger automatic notifications.

### Assigning Preparers and Reviewers

1. Open the tax return
2. In the Overview tab, click **Edit Assignment**
3. Select the preparer and/or reviewer
4. Save changes

### Adding Notes

1. Open the tax return
2. Go to the **Activity** tab
3. Enter your note in the text field
4. Check "Mark as important" if needed
5. Click **Add Note**

### Merging Duplicate Returns

If duplicate returns exist for the same client:

1. Open one of the returns
2. Click **More Actions** > **Merge Returns**
3. Select the duplicate return to merge
4. Confirm the merge

Documents and notes from both returns will be combined.

---

## Tips

- **Add notes frequently** - Future staff will appreciate context
- **Update status promptly** - Keeps clients informed via automatic notifications
- **Use the Documents tab** - Always verify documents before changing status
- **Check the Workflow tab** - See the complete history if you're unsure what happened
`,

  notifications: `# Notifications

The system sends automatic notifications to clients and staff when important events occur. This guide explains when notifications are sent and how to manage them.

---

## How Notifications Work

Notifications keep clients informed about their tax return progress and alert staff when action is needed. Most notifications are sent automatically when a return's status changes, but you can also send notifications manually.

### Notification Channels

| Channel | Description | Use Cases |
|---------|-------------|-----------|
| **Email** | HTML-formatted messages with branding | Detailed information, links, instructions |
| **SMS** | Text messages via Twilio | Urgent updates, quick alerts |
| **Both** | Email and SMS sent together | Critical updates (signature ready, e-file accepted) |

---

## Automatic Notifications

These notifications are sent automatically when specific events occur.

### Documents Pending

| | |
|---|---|
| **Triggered When** | Status changes to "Documents Pending" |
| **Sent To** | Client |
| **Channel** | Email + SMS |
| **Content** | Request to upload documents, link to client portal, list of needed documents (if available) |

**Example SMS:**
> Your tax documents are needed for your 2024 return. Please upload them at [portal link] or reply to this message with questions.

---

### Documents Received

| | |
|---|---|
| **Triggered When** | Status changes to "In Preparation" (all documents received) |
| **Sent To** | Preparer |
| **Channel** | Email |
| **Content** | Notification that all client documents are ready for preparation |

---

### Ready for Review

| | |
|---|---|
| **Triggered When** | Status changes to "In Review" |
| **Sent To** | Reviewer |
| **Channel** | Email |
| **Content** | Return is ready for quality review, link to return details |

---

### Changes Needed

| | |
|---|---|
| **Triggered When** | Reviewer requests changes (status back to "In Preparation") |
| **Sent To** | Preparer |
| **Channel** | Email |
| **Content** | Reviewer notes, required changes, link to return |

---

### Review Approved

| | |
|---|---|
| **Triggered When** | Reviewer approves return (status to "Ready for Signature") |
| **Sent To** | Preparer |
| **Channel** | Email |
| **Content** | Return approved, client will receive signature request |

---

### Ready for Signature

| | |
|---|---|
| **Triggered When** | Status changes to "Ready for Signature" |
| **Sent To** | Client |
| **Channel** | Email + SMS |
| **Content** | Return is ready, signature needed, link to e-signature (sent separately via Zoho Sign) |

**Example SMS:**
> Great news! Your 2024 tax return is ready. Please check your email for the signature request from Zoho Sign.

---

### E-File Accepted

| | |
|---|---|
| **Triggered When** | IRS accepts the e-filed return |
| **Sent To** | Client |
| **Channel** | Email + SMS |
| **Content** | Confirmation of IRS acceptance, what to expect next |

**Example SMS:**
> Your 2024 tax return has been accepted by the IRS! Check your email for details about your refund/payment.

---

### Results Summary

| | |
|---|---|
| **Triggered When** | Status changes to "Completed" |
| **Sent To** | Client |
| **Channel** | Email + SMS |
| **Content** | Final summary with refund/payment amount, expected dates, next steps |

---

## Notification Summary Table

| Notification | Trigger | Recipient | Channel |
|--------------|---------|-----------|---------|
| Documents Pending | Status → Documents Pending | Client | Email + SMS |
| Documents Received | Status → In Preparation | Preparer | Email |
| Ready for Review | Status → In Review | Reviewer | Email |
| Changes Needed | Review requests changes | Preparer | Email |
| Review Approved | Review approves | Preparer | Email |
| Ready for Signature | Status → Ready for Signature | Client | Email + SMS |
| E-File Accepted | IRS accepts return | Client | Email + SMS |
| Results Summary | Status → Completed | Client | Email + SMS |

---

## Manual Notifications

Sometimes you need to send a notification outside the automatic triggers.

### When to Send Manual Notifications

- Reminder to upload documents
- Urgent status update
- Custom message to client
- Resend a failed notification

### Sending a Manual Notification

1. Open the tax return
2. Click **Send Notification** or go to the **Notifications** section
3. Select the notification type
4. Choose the channel (Email, SMS, or Both)
5. Optionally customize the SMS message
6. Click **Send**

### Customizing Messages

When sending manual notifications, you can:

- Edit the SMS message text (email uses standard template)
- Add specific instructions or information
- Keep the message concise for SMS (160 character limit for single SMS)

---

## Viewing Notification History

Every notification sent is recorded in the system.

### Accessing History

1. Open the tax return
2. Go to the **Workflow** or **Activity** tab
3. View the notification history section

### History Details

Each notification record shows:

| Field | Description |
|-------|-------------|
| **Type** | The notification type (e.g., "Documents Pending") |
| **Recipient** | Who received it (Client, Preparer, Reviewer) |
| **Channel** | How it was sent (Email, SMS, Both) |
| **Status** | Sent, Failed, or Pending |
| **Sent Date** | When it was sent |
| **Error** | Error details if failed |

---

## Handling Failed Notifications

Notifications can fail for various reasons:

### Common Failure Reasons

| Issue | Cause | Solution |
|-------|-------|----------|
| Invalid email | Email address incorrect or bounced | Verify and update client email |
| Invalid phone | Phone number incorrect format | Verify and update phone number |
| Undeliverable SMS | Phone cannot receive SMS | Contact client via email instead |
| Service outage | Twilio or email service temporarily down | Wait and resend later |

### Resending Failed Notifications

1. Open the tax return
2. Find the failed notification in history
3. Click **Resend**
4. Or send a new manual notification

### Preventing Failures

- Verify client contact information when creating returns
- Ensure phone numbers include area code
- Confirm email addresses are current
- Check for typos in contact info

---

## Email Formatting

All emails include:

- PTC Financial branding and logo
- Clear subject line with client name and tax year
- Professional HTML formatting
- Action buttons where applicable (e.g., "Upload Documents")
- Contact information for questions
- Unsubscribe link (for marketing/outreach emails)

---

## SMS Best Practices

- Messages are kept concise (under 160 characters when possible)
- Include essential information only
- Provide a link for more details when needed
- Use a professional but friendly tone
`,

  outreach: `# Outreach

The Outreach feature enables you to send personalized email and SMS campaigns to clients for tax season communications, document reminders, and other important updates.

---

## Overview

Outreach campaigns help you:

- Kick off tax season with personalized communications
- Remind clients to submit required documents
- Share important tax law updates
- Collect client information via questionnaires
- Track engagement with opens and clicks

---

## Campaign Types

### Tax Season Kickoff

Send at the beginning of tax season (typically early January) to engage clients and encourage early document submission.

**Content includes:**
- Welcome message for the new tax year
- Important tax law changes
- Document checklist personalized from prior year
- Link to upload documents
- Life changes questionnaire

**Best timing:** Early to mid-January

---

### Document Reminders

Send to clients who haven't yet submitted their documents.

**Content includes:**
- Reminder of outstanding documents
- Specific documents still needed (from Drake checklist)
- Deadline reminders
- Easy upload link

**Best timing:** Throughout tax season, increasing urgency as deadlines approach

---

### Custom Campaigns

Create campaigns for specific purposes:

- Tax law update announcements
- Service changes
- Holiday closures
- Special promotions

---

## Creating an Email Campaign

### Step 1: Navigate to Outreach

Go to **Outreach** from the main menu.

### Step 2: Select Recipients

Choose which clients should receive the campaign:

- **All eligible clients** - Clients not opted out
- **Documents pending** - Clients still missing documents
- **By preparer** - Clients assigned to specific preparers
- **Custom selection** - Manually select clients

### Step 3: Choose Campaign Type

Select the type of campaign:

- Tax Season Kickoff
- Document Reminder
- Custom

### Step 4: Personalization

The system automatically personalizes emails with:

| Placeholder | Replaced With |
|-------------|---------------|
| {ClientName} | Client's full name |
| {TaxYear} | Current tax year |
| {PhoneNumber} | Office phone number |
| {UploadLink} | Personalized portal upload link |
| {DocumentList} | Required documents from Drake checklist |

### Step 5: Preview and Send

1. Preview the email to verify formatting
2. Send a test email to yourself (recommended)
3. Click **Send Campaign** to deliver to all recipients

---

## Creating an SMS Campaign

SMS campaigns are shorter and more immediate than email.

### Step 1: Navigate to Outreach

Go to **Outreach** from the main menu.

### Step 2: Select Recipients

Choose clients with valid phone numbers:

- System filters to clients with SMS-capable numbers
- Opted-out clients are automatically excluded

### Step 3: Compose Message

Write your SMS message:

- Keep under 160 characters for single SMS
- Include essential information only
- Add portal link if relevant

**Example:**
> Tax season is here! Upload your 2024 documents at [link]. Questions? Call us at 555-1234.

### Step 4: Send

1. Review recipient count
2. Click **Send SMS Campaign**

---

## Drake Checklist Integration

If Drake PDF extractions are available, outreach emails can include personalized document checklists.

### How It Works

1. Drake PDFs from prior year are uploaded and processed
2. System extracts document types needed (W-2s, 1099s, etc.)
3. Outreach emails include personalized checklist
4. Checklist shows specific issuers when available

### Example Personalized Checklist

> **Documents We'll Need from You:**
> - W-2 from ABC Company
> - W-2 from XYZ Corporation
> - 1099-INT from First National Bank
> - 1099-DIV from Vanguard
> - Mortgage Interest Statement from Wells Fargo
> - Property Tax Records

This personalization significantly improves client response rates.

---

## Tracking Campaign Performance

### Metrics Available

| Metric | Description |
|--------|-------------|
| **Sent** | Total emails/SMS delivered |
| **Opens** | Email opens (tracked via pixel) |
| **Open Rate** | Percentage of recipients who opened |
| **Clicks** | Link clicks in email |
| **Click Rate** | Percentage who clicked a link |
| **Questionnaire Responses** | Clients who completed questionnaire |
| **Documents Uploaded** | Clients who uploaded after receiving |

### Viewing Campaign Results

1. Go to **Outreach** > **History** or **Dashboard**
2. Select the campaign to view
3. See aggregate metrics and per-client details

### Per-Client Tracking

For each recipient, you can see:

- Whether they opened the email
- Which links they clicked
- If they completed the questionnaire
- If they uploaded documents

---

## Managing Client Opt-Outs

Clients can opt out of marketing communications. Opt-outs are managed separately from transactional notifications (like status updates).

### Viewing Opt-Outs

Go to **Client Opt-Outs** to see clients who have opted out.

### Opt-Out Reasons

- Moved away
- Using different preparer
- Filing taxes themselves (DIY)
- No longer need service
- Cost concerns
- Dissatisfied with service
- Deceased
- Other

### Recording an Opt-Out

1. Go to **Client Opt-Outs**
2. Click **Add Opt-Out**
3. Select the client
4. Choose the reason
5. Add notes if helpful
6. Save

### Reactivating a Client

If a client wants to return:

1. Find them in **Client Opt-Outs**
2. Click **Reactivate**
3. Add reactivation notes
4. Save

The client will now receive outreach campaigns again.

---

## CAN-SPAM Compliance

All marketing emails comply with CAN-SPAM requirements:

- **Unsubscribe link** - Every email includes an easy unsubscribe option
- **Physical address** - Company address included in footer
- **Accurate headers** - From address and subject accurately represent content
- **Opt-out honored** - Unsubscribes processed within 10 days (typically immediate)
- **No purchased lists** - Only existing clients receive campaigns

---

## Best Practices

### Timing

- **Tax Season Kickoff** - First week of January
- **Document Reminders** - Every 2-3 weeks until April 15
- **Urgent Reminders** - Week before deadlines

### Content

- Keep emails focused on one main action
- Use clear, simple language
- Include a prominent call-to-action button
- Personalize when possible

### Frequency

- Don't over-send (fatigue reduces effectiveness)
- Space campaigns at least 1 week apart
- Increase frequency only near deadlines

### SMS

- Reserve for urgent communications
- Keep messages very short
- Always include contact method for questions
- Consider time zones
`,

  documents: `# Documents

This guide covers how to manage client documents in the system, including uploads, Drake PDF processing, and document organization.

---

## Document Sources

Documents enter the system through three methods:

| Source | Description |
|--------|-------------|
| **Staff Upload** | Staff uploads documents through the dashboard |
| **Client Portal** | Client uploads through their portal link |
| **Email Processing** | Documents attached to client emails (automatic) |

---

## Staff Document Upload

### Uploading Documents

1. Open the tax return
2. Go to the **Documents** tab
3. Click **Upload Documents**
4. Drag and drop files or click to browse
5. Select the document type (optional, AI can classify)
6. Click **Upload**

### Supported File Types

- PDF (preferred)
- Images (JPG, PNG)
- Word documents (DOC, DOCX)
- Excel spreadsheets (XLS, XLSX)

### File Size Limits

- Maximum file size: 25 MB per file
- Multiple files can be uploaded at once

---

## Client Portal Uploads

Clients can upload documents directly through their personalized portal.

### How Clients Upload

1. Client receives upload link via email or SMS
2. Client clicks link to access their portal
3. Client authenticates via OTP or password
4. Client drags/drops or selects files
5. Documents appear in the system automatically

### Generating Upload Links

To create a personalized upload link for a client:

1. Open the tax return
2. Click **Generate Upload Link**
3. Copy the link
4. Send to client via email, SMS, or other method

### Upload Notifications

When clients upload documents:

- Documents appear in the return immediately
- If all required documents are received, a notification is sent to the preparer
- Upload activity is logged in the return history

---

## Email Document Processing

The system automatically processes documents attached to client emails.

### How It Works

1. Client sends email to the office inbox
2. System checks for attachments
3. Attachments are matched to the client's return
4. Documents are classified and stored

### Matching Logic

Emails are matched to returns based on:

- Sender email address
- Client name in email body
- Tax year references

### Processing Time

Email processing runs every 5 minutes. Documents typically appear within 10 minutes of the email being received.

---

## Document Classification

Documents are automatically classified using AI to identify the document type.

### Classification Categories

| Category | Examples |
|----------|----------|
| **Income** | W-2, 1099-NEC, 1099-MISC, 1099-K |
| **Investment** | 1099-INT, 1099-DIV, 1099-B, K-1 |
| **Deductions** | 1098 (Mortgage Interest), 1098-T, 1098-E |
| **Property** | Property tax statements, closing documents |
| **Healthcare** | 1095-A, 1095-B, 1095-C |
| **Business** | P&L statements, balance sheets, 1099s received |
| **Identity** | Driver's license, SSN card |
| **Prior Returns** | Prior year tax returns |
| **Other** | Miscellaneous supporting documents |

### Manual Classification

If AI classification is incorrect:

1. Open the document
2. Click **Edit**
3. Select the correct document type
4. Save

---

## Drake PDF Processing

Drake software outputs can be uploaded to extract document checklists and client information for use in outreach campaigns.

### What Drake Processing Does

1. Extracts document types from prior year return
2. Identifies specific issuers (employer names, bank names, etc.)
3. Creates personalized checklist for client outreach
4. Stores extraction results for reference

### Uploading Drake PDFs

1. Go to **Drake Upload** from the main menu
2. Click **Upload Drake PDF**
3. Select the Drake output file
4. Wait for processing to complete

### Batch Uploads

You can upload multiple Drake PDFs at once:

1. Go to **Drake Upload**
2. Click **Batch Upload**
3. Select multiple PDF files
4. System processes each file

### Matching to Clients

After upload, the system attempts to match Drake extractions to existing clients based on:

- Name
- SSN (last 4 digits)
- Address

If no match is found, you can manually match:

1. View unmatched extractions
2. Search for the correct client
3. Click **Match** to link them

### Password-Protected PDFs

Some Drake outputs are password-protected.

**To process protected PDFs:**

1. Upload the PDF
2. When prompted, enter the password
3. Processing continues after verification

**Common password formats:**
- Last 4 of SSN + ZIP code
- Client-specific password from Drake

---

## Document Checklist

The document checklist shows what documents are needed for a return.

### Viewing the Checklist

1. Open the tax return
2. Go to **Documents** tab
3. View the **Checklist** section

### Checklist Sources

Checklists can come from:

- Drake PDF extraction (personalized from prior year)
- Standard checklist (default documents for filing type)
- Manual additions (staff-added items)

### Checklist Status

Each checklist item shows:

| Status | Meaning |
|--------|---------|
| **Needed** | Document not yet received |
| **Received** | Document uploaded and matched |
| **Not Applicable** | Marked as not needed for this client |

---

## Viewing and Managing Documents

### Document List

The Documents tab shows all documents for a return:

- Document name
- Type/classification
- Upload date
- Source (staff, client, email)
- File size

### Document Preview

Click any document to preview:

- PDF viewer for PDF files
- Image viewer for images
- Download option for other types

### Downloading Documents

**Single document:**
1. Click the document
2. Click **Download**

**All documents:**
1. In Documents tab, click **Download All**
2. System creates a ZIP file with all documents

### Deleting Documents

1. Select the document
2. Click **Delete**
3. Confirm deletion

> **Note:** Deleted documents cannot be recovered. Only delete duplicates or incorrect uploads.

---

## Document Security

### Access Control

- Only staff with access to a return can view its documents
- Client portal only shows documents the client uploaded
- All document access is logged

### Storage

- Documents stored in Azure Blob Storage
- Encrypted at rest and in transit
- Retained according to company policy

---

## Tips

- **Name files clearly** when uploading (e.g., "W2_Employer_2024.pdf")
- **Check the checklist** before contacting clients about missing documents
- **Use Drake extraction** to create personalized document requests
- **Process emails promptly** to ensure documents are matched correctly
- **Verify classifications** to ensure accuracy in return preparation
`,

  'client-portal': `# Client Portal

The client portal allows your clients to upload documents, check their return status, and complete e-signatures without needing to call or visit the office.

---

## Overview

The client portal provides clients with:

- Document upload capability
- Return status visibility
- E-signature access
- Secure communication

The portal is separate from the staff application and requires no login credentials for first-time access.

---

## How Clients Access the Portal

### Personalized Links

Clients access the portal through personalized links sent via:

- Email notifications
- SMS messages
- Manual links shared by staff

Each link is unique to the client and their tax return.

---

## Client Authentication

The portal uses secure authentication to verify client identity.

### One-Time Password (OTP)

**For most portal access:**

1. Client clicks the portal link
2. System sends OTP to client's phone (SMS) or email
3. Client enters the 6-digit code
4. Client gains access to the portal

**OTP features:**
- Codes expire after 10 minutes
- Limited retry attempts
- New code can be requested if expired

### Password Login (Returning Users)

Clients can optionally set a password for faster future access:

1. After OTP verification, client can create a password
2. Next visit, they can use password instead of OTP
3. Password reset available via OTP if forgotten

### Device Verification

For added security:

- New devices require verification
- Trusted devices remembered for 30 days
- Suspicious access triggers additional verification

---

## Portal Features

### Document Upload

Clients can upload documents directly:

1. Click **Upload Documents**
2. Drag and drop files or click to browse
3. Add optional notes about the documents
4. Click **Upload**

**Supported files:**
- PDF, JPG, PNG, DOC, DOCX
- Maximum 25 MB per file
- Multiple files can be uploaded at once

### Status Tracking

Clients can view their return's current status:

| Status | What Client Sees |
|--------|------------------|
| Scheduled | "Your appointment is scheduled" |
| Documents Pending | "We're waiting for your documents" |
| In Preparation | "Your return is being prepared" |
| In Review | "Your return is being reviewed" |
| Ready for Signature | "Your return is ready to sign" |
| E-Filed | "Your return has been filed with the IRS" |
| Completed | "Your return is complete" |

### E-Signature

When a return is ready for signature:

1. Client receives notification (email + SMS)
2. Client accesses portal or clicks link in notification
3. Zoho Sign signature request is accessible
4. Client reviews and signs electronically
5. Signed document is recorded in the system

### Document Checklist

If a personalized checklist is available, clients see:

- List of documents needed
- Which documents have been received
- Which documents are still pending

This helps clients know exactly what to upload.

---

## Generating Portal Links for Clients

Staff can create portal links to share with clients.

### Creating an Upload Link

1. Open the client's tax return
2. Click **Generate Upload Link**
3. Link is created and displayed
4. Copy and share via email, SMS, or other method

### Link Options

When generating links, you can:

- Set expiration (default: 30 days)
- Add a custom message shown in the portal
- Restrict to specific actions (upload only, status only)

### Resending Links

If a client loses their link:

1. Open the return
2. Click **Generate Upload Link** again
3. A new link is created (old link still works if not expired)
4. Share the new link

---

## Portal Security

### Data Protection

- All portal traffic encrypted (HTTPS)
- OTP verification prevents unauthorized access
- Session timeout after 30 minutes of inactivity
- No sensitive data displayed without authentication

### Access Logging

All portal access is logged:

- When client accessed the portal
- What actions they took
- IP address and device information

This helps identify any suspicious activity.

### Best Practices for Staff

- Never share portal links via unsecured channels
- Verify client identity before generating new links
- Monitor for unusual access patterns
- Report suspected security issues immediately

---

## Common Client Questions

### "I didn't receive the code"

**For SMS:**
- Verify phone number is correct
- Check if phone can receive SMS
- Try requesting a new code
- Use email option instead

**For Email:**
- Check spam/junk folder
- Verify email address is correct
- Request a new code
- Add sender to contacts

### "My link expired"

Links expire for security. To resolve:

1. Staff generates a new portal link
2. New link is sent to client
3. Client uses new link to access portal

### "I can't upload my document"

Common issues:

| Problem | Solution |
|---------|----------|
| File too large | Reduce file size or split into multiple files |
| Wrong file type | Convert to PDF or supported format |
| Browser issue | Try different browser or clear cache |
| Connection error | Check internet and retry |

### "I signed but it still shows pending"

E-signature processing may take a few minutes. If status doesn't update:

1. Wait 10-15 minutes
2. Refresh the portal page
3. Contact staff if still showing pending

---

## Mobile Access

The client portal is mobile-friendly:

- Responsive design works on phones and tablets
- Camera upload supported on mobile devices
- All features available on mobile

### Mobile Upload Tips for Clients

- Use phone camera to capture documents
- Ensure good lighting and focus
- Upload immediately after capturing
- Verify upload completed successfully
`,

  integrations: `# Integrations

The PTC Tax Lifecycle application connects with several external services to provide email, SMS, e-signatures, and data synchronization. This guide explains what each integration does and how data flows through the system.

---

## Integration Overview

| Service | Purpose | Data Shared |
|---------|---------|-------------|
| **Microsoft 365** | Send email notifications | Email addresses, notification content |
| **Twilio** | Send SMS/WhatsApp messages | Phone numbers, message content |
| **Dynamics 365 (Dataverse)** | Sync client and appointment data | Contact details, appointments |
| **Zoho Sign** | Electronic signatures | Form 8879, client email |
| **Azure OpenAI** | Document classification | Document text (for analysis) |

---

## Microsoft 365 (Microsoft Graph)

### What It Does

Microsoft 365 integration handles all email communication from the application.

### Emails Sent

| Email Type | When Sent |
|------------|-----------|
| Status notifications | When return status changes |
| Document requests | When documents are needed |
| Outreach campaigns | Tax season kickoff, reminders |
| Staff notifications | Review ready, changes needed |

### Email Features

- **HTML formatting** - Professional, branded emails
- **Attachments** - Documents can be attached when needed
- **Tracking** - Open tracking for outreach campaigns
- **Templates** - Consistent messaging across all emails

### Security

- OAuth 2.0 authentication
- No passwords stored in the application
- Microsoft handles email delivery and security

---

## Twilio

### What It Does

Twilio sends SMS text messages and WhatsApp messages to clients.

### Messages Sent

| Message Type | When Sent |
|--------------|-----------|
| Document requests | When documents are pending |
| Signature ready | When return needs signature |
| E-file accepted | When IRS accepts return |
| Results summary | When return is complete |
| OTP verification | When client accesses portal |
| Outreach campaigns | Tax season SMS campaigns |

### Phone Number Handling

- Numbers automatically formatted to international standard (+1 for US)
- Invalid numbers flagged for staff review
- Delivery status tracked

### Security

- Messages encrypted in transit
- No message content stored by Twilio long-term
- Account secured with authentication tokens

---

## Dynamics 365 (Dataverse)

### What It Does

Dynamics 365 is the source of client and appointment information. The application syncs data from Dataverse to maintain current records.

### Data Synced

**From Dataverse to Application:**

| Data | Purpose |
|------|---------|
| Contacts | Client names, emails, phones, addresses |
| Appointments | Scheduled tax appointments |
| Accounts | Business client information |
| Services | Appointment types and services requested |

### Sync Schedule

- **Appointment sync** runs hourly
- New appointments automatically create tax returns
- Contact updates reflected in client information

### Two-Way Sync

Some data flows back to Dataverse:

- Return status updates (for CRM visibility)
- Document receipt confirmation
- Completion status

### Accessing Dataverse Records

Staff can open linked Dataverse records directly:

1. Open a tax return
2. Click the **Open in Dynamics** button
3. Record opens in Dynamics 365

This is useful for updating contact information or viewing full CRM history.

---

## Zoho Sign

### What It Does

Zoho Sign handles electronic signatures for tax authorization forms (Form 8879).

### E-Signature Workflow

1. Return moves to "Ready for Signature"
2. Application creates signature request in Zoho Sign
3. Client receives email from Zoho Sign with signing link
4. Client reviews and signs the document
5. Zoho Sign notifies application via webhook
6. Signed document stored in application
7. Return moves toward e-filing

### Documents Signed

| Form | Description |
|------|-------------|
| Form 8879 | Individual e-file authorization |
| Form 8879-C | C-Corporation authorization |
| Form 8879-S | S-Corporation authorization |
| Form 8879-PE | Partnership authorization |

### Multi-Signer Support

For joint returns:

1. Primary taxpayer signs first
2. Spouse receives signing request
3. Both signatures collected
4. Return proceeds when all signed

### Security

- Documents encrypted at rest and in transit
- Audit trail maintained by Zoho Sign
- Signed documents tamper-evident

---

## Azure OpenAI

### What It Does

Azure OpenAI provides AI-powered document classification to automatically identify uploaded document types.

### How Classification Works

1. Client uploads a document
2. Document text extracted (OCR if needed)
3. AI analyzes content to determine document type
4. Classification applied (W-2, 1099, etc.)
5. Document tagged for preparer reference

### Privacy Considerations

- Only document text sent (not images)
- No personal data stored by AI service
- Processing occurs in Azure's secure environment

### Classification Accuracy

AI classification is typically accurate but can be manually corrected:

1. View document in return
2. Click **Edit Classification**
3. Select correct type
4. Save

---

## Email Inbox Processing

### What It Does

The application monitors the office email inbox for client emails with attachments.

### How It Works

1. System checks inbox every 5 minutes
2. Emails with attachments identified
3. Sender matched to client record
4. Attachments extracted and stored
5. Documents linked to client's return
6. Email marked as processed

### Matching Logic

Emails matched based on:

- Sender email address
- Client name in email body
- Tax year references

### Unmatched Emails

If an email can't be auto-matched:

- Placed in review queue
- Staff manually matches to correct client
- Or creates new client record if needed

---

## Integration Status

To check if integrations are working properly:

### Email (Microsoft 365)

- Check notification history for send status
- Failed emails show error details
- Contact admin if persistent failures

### SMS (Twilio)

- Check notification history for delivery status
- Invalid phone numbers flagged
- Delivery confirmation available

### Dataverse Sync

- New appointments should appear within 1 hour
- Contact updates reflected after next sync
- Manual refresh available in some views

### Zoho Sign

- Signature requests show status in return
- Check Zoho Sign portal for detailed status
- Webhook issues flagged in system logs
`,

  troubleshooting: `# Troubleshooting

This guide covers common issues and their solutions.

---

## Notification Issues

### "Notification failed to send"

Email or SMS notifications may fail for several reasons.

**Check email address validity:**
1. Open the tax return
2. Verify the client's email address is correct
3. Look for typos or invalid formats
4. Update if needed and resend notification

**Check phone number validity:**
1. Open the tax return
2. Verify the phone number includes area code
3. Ensure it's a mobile number (for SMS)
4. Format should be 10 digits (e.g., 5551234567)
5. Update if needed and resend

**Common email issues:**

| Problem | Solution |
|---------|----------|
| Invalid email format | Correct the email address |
| Email bounced | Contact client for updated email |
| Mailbox full | Client needs to clear their inbox |
| Spam filtered | Ask client to check spam folder |

**Common SMS issues:**

| Problem | Solution |
|---------|----------|
| Invalid phone number | Correct the phone number |
| Landline number | Get mobile number for SMS |
| Number not in service | Contact client for current number |
| Carrier blocking | Try email instead |

### Resending a Failed Notification

1. Open the tax return
2. View notification history
3. Find the failed notification
4. Click **Resend** after fixing the issue

---

## Document Upload Issues

### "Document upload failed"

**File too large:**
- Maximum size is 25 MB per file
- Reduce file size by:
  - Compressing PDF
  - Reducing image resolution
  - Splitting into multiple files

**Unsupported file type:**
- Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX
- Convert other formats to PDF before uploading

**Browser issues:**
- Try a different browser
- Clear browser cache
- Disable browser extensions temporarily
- Check internet connection

**Server timeout:**
- Check internet connection stability
- Try uploading smaller files
- Try again during off-peak hours

### Client Can't Upload via Portal

1. Verify the portal link is valid and not expired
2. Check if client completed OTP verification
3. Ensure client is using a supported browser
4. Have client try a different device
5. Generate a new portal link if needed

---

## Drake Upload Issues

### "Drake extraction incomplete"

**Password-protected PDF:**
1. The system will prompt for the password
2. Enter the Drake password (usually last 4 SSN + ZIP)
3. Retry the extraction
4. If password unknown, contact whoever generated the PDF

**Corrupted PDF:**
- Re-export the PDF from Drake software
- Ensure complete download before uploading
- Try opening the PDF locally to verify it's valid

**Missing data:**
- Some Drake outputs have limited data
- Manual entry may be required for some fields
- Check if correct Drake report was uploaded

### "Client not matched"

When Drake extractions don't automatically match to clients:

1. Go to **Drake Upload** > **Unmatched**
2. View the extraction details
3. Search for the client in the system
4. Click **Match** to link them
5. If client doesn't exist, create a new client record

---

## Dataverse/Dynamics Issues

### "Client not found in Dataverse"

The client exists in Tax Lifecycle but not in Dynamics 365.

**To create in Dataverse:**
1. Open Dynamics 365
2. Create a new Contact record
3. Enter client information
4. Save the contact
5. The Tax Lifecycle record will sync on next cycle

**To link existing Dataverse contact:**
1. Open the tax return
2. Click **Link to Dataverse**
3. Search for the contact in Dataverse
4. Select and link

### "Appointment not syncing"

If appointments aren't appearing in Tax Lifecycle:

1. Verify the appointment exists in Dynamics 365
2. Check the appointment is for tax preparation service
3. Ensure the appointment date is in the current tax season
4. Wait for the next hourly sync
5. Contact admin if still not appearing

---

## E-Signature Issues

### "E-signature request failed"

**Document validation errors:**
- Form 8879 must be properly formatted
- Ensure all required fields are populated
- Check for PDF compatibility issues

**Client email issues:**
- Verify client email is correct
- Zoho Sign sends directly to client email
- Check spam folder for Zoho Sign emails

**To retry:**
1. Open the tax return
2. Go to signature section
3. Click **Resend Signature Request**

### "Client says they signed but status not updated"

Zoho Sign webhook may be delayed:

1. Wait 10-15 minutes
2. Refresh the return page
3. Check Zoho Sign portal for actual status
4. Contact admin if webhook appears stuck

### "Spouse didn't receive signature request"

For joint returns with multiple signers:

1. Primary signer must complete first
2. Spouse receives request after primary signs
3. Verify spouse email is correct
4. Check spouse's spam folder

---

## Client Portal Issues

### "Client can't log in"

**OTP not received:**
1. Verify phone/email is correct
2. Ask client to check spam folder (email)
3. Confirm mobile can receive SMS
4. Generate a new portal link
5. Try alternate verification method

**Link expired:**
1. Portal links expire after 30 days
2. Generate a new link for the client
3. Send via email or SMS

**Browser issues:**
- Try different browser (Chrome, Edge, Firefox)
- Clear cache and cookies
- Disable ad blockers
- Enable JavaScript

### "Client uploaded wrong document"

1. Open the tax return
2. Go to **Documents** tab
3. Delete the incorrect document
4. Contact client to upload correct document
5. Or upload correct document on their behalf

---

## Status Update Issues

### "Can't change status"

**Status workflow restrictions:**
- Status changes must follow the workflow
- Can't skip stages (e.g., directly from Documents Pending to E-Filed)
- Some changes require specific conditions

**Required conditions:**

| Target Status | Requirements |
|---------------|--------------|
| In Preparation | Documents must be uploaded |
| In Review | Must have preparer assigned |
| Ready for Signature | Must be approved by reviewer |
| E-Filed | Must have signature completed |
| Completed | Must have IRS acceptance |

**Permissions:**
- Some status changes require reviewer or admin role
- Check your user role if unable to make changes

---

## Performance Issues

### "Application is slow"

**Browser fixes:**
- Clear browser cache
- Close unnecessary tabs
- Restart browser
- Try a different browser

**Connection issues:**
- Check internet connection speed
- Try wired connection instead of WiFi
- Restart router if needed

**Peak usage:**
- Application may be slower during peak hours
- Try again during off-peak times

### "Search is slow"

When searching large datasets:

- Use filters to narrow results before searching
- Search by specific fields (name, ID) rather than general search
- Clear any unnecessary filters first

---

## Getting Help

### Before Contacting Support

Gather this information:
1. What you were trying to do
2. The exact error message (screenshot if possible)
3. The tax return ID (if applicable)
4. When the issue occurred
5. What you've already tried

### Contacting Support

- **Email**: support@ptcfinancial.com
- **Include**: All information gathered above
- **Screenshots**: Attach any relevant screenshots

### Emergency Issues

For urgent issues affecting tax filing deadlines:
- Contact your supervisor immediately
- Note the return ID and client name
- Document the issue for follow-up

---

## Error Message Reference

| Error | Meaning | Action |
|-------|---------|--------|
| "Network error" | Internet connection issue | Check connection, retry |
| "Session expired" | Login session timed out | Log in again |
| "Permission denied" | Insufficient access rights | Contact admin |
| "Record not found" | Item deleted or moved | Refresh, search again |
| "Validation failed" | Required data missing | Fill in required fields |
| "Service unavailable" | External service down | Wait and retry |

---

## Preventing Issues

### Best Practices

1. **Verify contact info** when creating returns
2. **Use correct formats** for phone (10 digits) and email
3. **Check documents** before sending for signature
4. **Save work frequently** to avoid losing changes
5. **Keep browser updated** for best compatibility

### Regular Maintenance

- Clear browser cache weekly
- Log out and back in if experiencing issues
- Report recurring problems to admin
`,
};
